import { useEffect, useMemo, useState } from 'react';
import type { ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Checkbox,
  EmptyState,
  Input,
  ReportTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
} from '@roger/ui-components';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';
import { PermissionSlug } from '../constants/featureFlags';
import { useRecertificationTimingReportParams } from '../hooks/reportParams/useRecertificationTimingReportParams';
import {
  exportRecertificationTimingReportReportsRecertificationTimingExportGet,
  getRecertificationTimingOptionsReportsRecertificationTimingOptionsGet,
  getRecertificationTimingReportReportsRecertificationTimingGet,
} from '../services/api/sdk.gen';
import type {
  RecertificationTimingReportRow,
  RecertificationTimingSortBy,
  RecertificationTimingSortDirection,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { formatDateToMMDDYYYY } from '../utils/dateUtils';
import { coerceReportPageSize, displayDash } from '../utils/reportDisplay';
import { ReportLink } from '../components/reporting/ReportLink';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';

const ALL_CLINICS_VALUE = '__all_clinics__';
const patientName = (row: RecertificationTimingReportRow) =>
  `${row.patient_last_name}, ${row.patient_first_name}`;

const columns: ReportTableColumn<RecertificationTimingReportRow>[] = [
  {
    id: 'patient',
    header: 'Patient',
    accessor: patientName,
    render: (_value, row) => (
      <ReportLink to={`/patients/${row.patient_id}`}>
        {patientName(row)}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 190,
  },
  {
    id: 'clinic_name',
    header: 'Clinic',
    accessor: row => displayDash(row.clinic_name),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'episode_start_date',
    header: 'Episode Start',
    accessor: row => formatDateToMMDDYYYY(row.episode_start_date),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'episode_end_date',
    header: 'Episode End',
    accessor: row => formatDateToMMDDYYYY(row.episode_end_date),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'assessment_date',
    header: 'Assessment Date',
    accessor: row =>
      row.assessment_date ? formatDateToMMDDYYYY(row.assessment_date) : '—',
    enableSorting: true,
    minWidth: 155,
  },
  {
    id: 'episode_day',
    header: 'Episode Day',
    accessor: row => row.episode_day ?? '—',
    align: 'right',
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'timing_status',
    header: 'Timing',
    accessor: row => (row.timing_status === 'pass' ? 'Pass' : 'Fail'),
    enableSorting: true,
    minWidth: 110,
  },
];

export default function RecertificationTimingReportPage() {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) {
    return <ReportsPermissionAlert />;
  }
  return (
    <div className='flex h-full flex-col'>
      <div className='flex-1 overflow-auto'>
        <RecertificationTimingReport />
      </div>
    </div>
  );
}

export function RecertificationTimingReport() {
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const [clinicOptionsError, setClinicOptionsError] = useState(false);
  const [rows, setRows] = useState<RecertificationTimingReportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reportsBackHref = useReportsBackHref();
  const {
    params,
    searchInput,
    setSearchInput,
    setFilters,
    setSorting,
    setPagination,
    isSearchPending,
    tableQuery,
    exportQuery: normalizedExportQuery,
  } = useRecertificationTimingReportParams();
  const page = params.page > 0 ? params.page : 1;
  const pageSize = coerceReportPageSize(params.pageSize);
  const sortBy = params.sortBy as RecertificationTimingSortBy;
  const sortDirection =
    params.sortDirection as RecertificationTimingSortDirection;
  const reportQuery = useMemo(
    () => ({ ...tableQuery, page, page_size: pageSize }),
    [tableQuery, page, pageSize]
  );
  const {
    page: _exportPage,
    page_size: _exportPageSize,
    ...exportQuery
  } = normalizedExportQuery;
  void _exportPage;
  void _exportPageSize;

  useEffect(() => {
    let cancelled = false;
    getRecertificationTimingOptionsReportsRecertificationTimingOptionsGet()
      .then(response => {
        if (cancelled) return;
        if (response.data) {
          setClinics(response.data.clinics);
          setClinicOptionsError(false);
        } else {
          setClinicOptionsError(true);
        }
      })
      .catch(error => {
        console.error('Failed to load Recertification Timing options', error);
        if (!cancelled) setClinicOptionsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getRecertificationTimingReportReportsRecertificationTimingGet({
      query: reportQuery,
    })
      .then(response => {
        if (cancelled) return;
        if (!response.data || response.error) {
          setRows([]);
          setTotal(0);
          setError(
            getBusinessErrorMessage({
              response: {
                status: response.response?.status,
                data: response.error,
              },
            }) ?? 'Unable to load recertification timing. Please try again.'
          );
          return;
        }
        setRows(response.data.items);
        setTotal(response.data.total);
      })
      .catch(error => {
        console.error('Failed to load Recertification Timing report', error);
        if (!cancelled) {
          setRows([]);
          setTotal(0);
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load recertification timing. Please try again.'
          );
        }
      })
      .finally(() => !cancelled && setIsLoading(false));
    return () => {
      cancelled = true;
    };
  }, [reportQuery]);

  const download = async () => {
    setIsExporting(true);
    try {
      const response =
        await exportRecertificationTimingReportReportsRecertificationTimingExportGet(
          {
            query: exportQuery,
            responseType: 'blob',
          }
        );
      if (!response.data || response.error) {
        setError(
          await getUserFacingApiMessageAsync(
            {
              response: {
                status: response.response?.status,
                data: response.response?.data ?? response.error,
              },
            },
            'Unable to download recertification timing. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(
        response as never,
        'recertification-timing-report.xlsx'
      );
    } catch (error) {
      console.error('Failed to download Recertification Timing report', error);
      setError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to download recertification timing. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  const sorting: ReportTableSort = { id: sortBy, direction: sortDirection };
  const pagination: ReportTablePaginationState = { page, pageSize };
  return (
    <div className='space-y-4'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-end'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void download()}
          disabled={isLoading || isExporting || isSearchPending}
        >
          {isExporting ? (
            <Loader2 className='h-4 w-4 animate-spin' />
          ) : (
            <Download className='h-4 w-4' />
          )}
          Export
        </Button>
      </div>
      <div className='flex flex-col gap-3 rounded-lg border bg-card p-4 md:flex-row md:items-end'>
        <label className='space-y-1 text-sm font-medium'>
          Clinic
          <Select
            disabled={clinicOptionsError}
            value={params.clinicId || ALL_CLINICS_VALUE}
            onValueChange={(value: string) =>
              setFilters({
                clinicId: value === ALL_CLINICS_VALUE ? '' : value,
              })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_CLINICS_VALUE}>All clinics</SelectItem>
              {clinics.map(clinic => (
                <SelectItem key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {clinicOptionsError && (
            <p className='text-sm font-normal text-destructive' role='alert'>
              Clinics could not be loaded. Refresh the page to try again.
            </p>
          )}
        </label>
        <label className='space-y-1 text-sm font-medium'>
          Patient name or MRN
          <Input
            value={searchInput}
            placeholder='Patient name or MRN'
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              setSearchInput(event.target.value)
            }
          />
        </label>
        <label className='flex items-center gap-2 pb-2 text-sm font-medium'>
          <Checkbox
            checked={params.onlyFailed}
            onCheckedChange={checked =>
              setFilters({ onlyFailed: checked === true })
            }
          />
          Show failures only
        </label>
      </div>
      <ReportTable
        data={rows}
        columns={columns}
        getRowId={row => row.visit_id}
        caption='Recertification timing'
        sorting={sorting}
        onSortingChange={next =>
          setSorting(
            (next?.id ?? 'assessment_date') as RecertificationTimingSortBy,
            next?.direction ?? 'asc'
          )
        }
        isLoading={isLoading}
        emptyState={
          <EmptyState
            title={error ?? 'No recertifications match these filters'}
            description='Adjust filters and try again.'
          />
        }
        pagination={{
          ...pagination,
          total,
          totalPages: Math.ceil(total / pageSize),
        }}
        onPaginationChange={next => setPagination(next.page, next.pageSize)}
      />
    </div>
  );
}
